import {
  Group,
  Heading,
  IconButton,
  OnlyShow,
  Paper,
  createStyles,
} from '@ingeniorforeningen/figurine-core';
import { Divider } from '@mantine/core';
import { stripText } from '../../helpers/textsHelpers';
import CaseFiles from './CaseFiles';
import CaseInformation from './CaseInformation';
import {
  AppLanguage,
  CaseDetails,
  CaseDocument,
  DetailsAndFiles,
  DetailsTexts,
  FilesTexts,
} from '../../types';
import SkeletonDetails from '../Skeleton/SkeletonDetails';

const style = createStyles((theme) => ({
  wrapper: {
    [theme.fn.smallerThan('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    [theme.fn.largerThan('sm')]: {
      overflow: 'hidden',
    },
  },
  header: {
    background: theme.other.colorTokens.backgrounds.primary,
    height: '4.5em',
    width: '100%',
  },
  closeButton: {
    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },
  divider: {
    color: theme.other.colorTokens.dividers.light,
    borderTopColor: theme.other.colorTokens.dividers.light,
  },
  scrollArea: {
    [theme.fn.smallerThan('sm')]: {
      overscrollBehavior: 'contain',
      overflow: 'auto',
    },
    [theme.fn.largerThan('sm')]: {
      overflow: 'hidden',
    },
  },
}));

type DetailsProps = {
  closeDraw: () => void;
  loading: boolean;
  language: AppLanguage;
  detailsAndFilesTexts?: DetailsAndFiles;
  caseDetails: CaseDetails | null;
  caseDocuments: CaseDocument[] | null;
};

export const Details = ({
  closeDraw,
  loading,
  language,
  detailsAndFilesTexts,
  caseDetails,
  caseDocuments,
}: DetailsProps) => {
  const { classes } = style();
  const detailsTexts = Object.assign({} as DetailsTexts, detailsAndFilesTexts);
  const filesTexts = Object.assign({} as FilesTexts, detailsAndFilesTexts);

  if (loading || !detailsAndFilesTexts || caseDetails === null || caseDocuments === null) {
    return (
      <Group justify="center" align="center" m={0}>
        <SkeletonDetails />
      </Group>
    );
  }

  return (
    <Paper p={0} className={classes.wrapper}>
      <Group className={`${classes.header} hidden-in-print`} p="xl" noWrap align="center">
        <OnlyShow smallerThan={'sm'}>
          <IconButton
            icon="ChevronRight"
            onClick={() => closeDraw()}
            className={classes.closeButton}
          />
        </OnlyShow>
        <Heading type="h3" text={stripText(detailsAndFilesTexts.header)} variant="light" />
      </Group>
      <Group py="sm" direction="column" className={classes.scrollArea}>
        <CaseInformation caseDetails={caseDetails} texts={detailsTexts} language={language} />
        {caseDocuments.length > 0 && (
          <>
            <Divider className={`${classes.divider} hidden-in-print`} />
            <CaseFiles
              caseId={caseDetails.caseId}
              caseDocuments={caseDocuments}
              texts={filesTexts}
            />
          </>
        )}
      </Group>
    </Paper>
  );
};
